import { useRef, useState } from "react";

import { DragEndEvent } from "@dnd-kit/core";
import { FieldErrors, useFieldArray, UseFormReturn } from "react-hook-form";
import { v4 } from "uuid";

import { buildEmptyPrize } from "./prizeFormData";
import { StepPrizesFormData } from "./types";

export const usePrizeList = (
  form: UseFormReturn<StepPrizesFormData>,
  initiallyExpandedPrizeId: string | null,
  finalizePendingImage?: (prizeId: string) => Promise<void>
) => {
  const { fields, insert, remove, move } = useFieldArray({
    control: form.control,
    name: "prizes",
    keyName: "fieldKey",
  });
  const [expandedPrizeId, setExpandedPrizeId] = useState<string | null>(initiallyExpandedPrizeId);
  const isDuplicatingRef = useRef(false);

  // An in-flight upload writes its URL by index, so reordering the list meanwhile would land it on another prize.
  const isListLocked = () => isDuplicatingRef.current || form.getValues("isImageUploading");

  const toggleExpanded = (prizeId: string) =>
    setExpandedPrizeId((currentPrizeId) => (currentPrizeId === prizeId ? null : prizeId));

  const addPrize = (position = fields.length) => {
    if (isListLocked()) {
      return;
    }
    const prize = buildEmptyPrize();
    insert(position, prize, { shouldFocus: false });
    setExpandedPrizeId(prize.id);
  };

  // A pending crop is only a data: preview, so it is uploaded first and the copy gets the hosted URL.
  const duplicatePrize = async (index: number) => {
    if (isListLocked()) {
      return;
    }
    isDuplicatingRef.current = true;
    try {
      await finalizePendingImage?.(form.getValues(`prizes.${index}.id`));
      const prize = { ...form.getValues(`prizes.${index}`), id: v4() };
      insert(index + 1, prize, { shouldFocus: false });
      setExpandedPrizeId(prize.id);
    } finally {
      isDuplicatingRef.current = false;
    }
  };

  const deletePrize = (index: number) => {
    if (!isListLocked()) {
      remove(index);
    }
  };

  const expandFirstInvalidPrize = (errors: FieldErrors<StepPrizesFormData>) => {
    const invalidPrize = fields.find((_field, index) => errors.prizes?.[index]);
    if (invalidPrize) {
      setExpandedPrizeId(invalidPrize.id);
    }
  };

  const movePrize = (fromIndex: number, toIndex: number) => {
    if (!isListLocked()) {
      move(fromIndex, toIndex);
    }
  };

  const movePrizeUp = (index: number) => movePrize(index, index - 1);

  const movePrizeDown = (index: number) => movePrize(index, index + 1);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const fromIndex = fields.findIndex((field) => field.id === active.id);
    const toIndex = fields.findIndex((field) => field.id === over.id);
    movePrize(fromIndex, toIndex);
  };

  return {
    prizes: fields,
    expandedPrizeId,
    toggleExpanded,
    addPrize,
    duplicatePrize,
    deletePrize,
    expandFirstInvalidPrize,
    movePrizeUp,
    movePrizeDown,
    onDragEnd,
  };
};

export type PrizeList = ReturnType<typeof usePrizeList>;
